// Status flags for the Platform map.
//
// Two trustworthy sources only:
//  1. "verified": an editor confirmed releaseStatus against official docs
//     (availabilityVerifiedAt + https availabilitySourceUrl).
//  2. "docs": the status is stated explicitly in the TITLE of an official
//     release note (docs.databricks.com / learn.microsoft.com), e.g. "(Beta)",
//     "is now generally available", "is retired". Blogs and marketing copy are
//     never used.
// "New" is separate: published within NEW_WINDOW_DAYS.
import type { ReleaseStatus, Story } from "./model.ts";

export const FLAG_STATUSES = ["private_preview", "public_preview", "beta", "ga", "deprecated", "retired"] as const;
export type FlagStatus = (typeof FLAG_STATUSES)[number];

export const FLAG_LABELS: Record<FlagStatus | "new", string> = {
  new: "New",
  private_preview: "Private Preview",
  public_preview: "Public Preview",
  beta: "Beta",
  ga: "GA",
  deprecated: "Deprecating",
  retired: "Retired",
};

export const NEW_WINDOW_DAYS = 30;
export const DOCS_LABEL_HOSTS = ["docs.databricks.com", "learn.microsoft.com"];

export interface DocsLabel {
  status: FlagStatus;
  sourceUrl: string;
}

const RULES: [FlagStatus, RegExp][] = [
  // Order matters: an "end of life for the … Beta endpoint" is a deprecation.
  ["deprecated", /\bupcoming (deprecation|end of (life|support)|retirement)\b|\bscheduled for (retirement|removal|deprecation)\b|\bwill be (retired|removed|deprecated)\b|\bis (now )?deprecated\b|\bdeprecation of\b/i],
  ["retired", /\b(is|are|has been|have been) (now )?(retired|removed)\b|\bretired$|\bremoved$/i],
  ["ga", /\((ga|general availability|generally available)\)\s*$|\b(is|are) (now )?generally available\b|\b(is|are) now ga\b/i],
  ["private_preview", /\(private preview\)\s*$|\bin private preview\b/i],
  ["public_preview", /\(public preview\)\s*$|\b(in|available in) (public )?preview\b/i],
  ["beta", /\(beta\)\s*$|\b(is|are) (now )?(available )?in beta\b|\bavailable in beta\b/i],
];

/** Explicit status from an official release-note title, or null. */
export function docsStatusFromTitle(title: string): FlagStatus | null {
  for (const [status, re] of RULES) if (re.test(title.trim())) return status;
  return null;
}

export function isOfficialDocsUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && DOCS_LABEL_HOSTS.includes(u.hostname);
  } catch {
    return false;
  }
}

export function flagFromReleaseStatus(s: ReleaseStatus): FlagStatus | null {
  return (FLAG_STATUSES as readonly string[]).includes(s) ? (s as FlagStatus) : null;
}

export interface EffectiveFlag {
  status: FlagStatus;
  source: "verified" | "docs";
  url: string;
}

/** Verified status wins; otherwise an official release-note title label; otherwise none. */
export function effectiveFlag(s: Story): EffectiveFlag | null {
  const verified = flagFromReleaseStatus(s.releaseStatus);
  if (verified && !s.isSample && s.availabilityVerifiedAt !== null && s.availabilitySourceUrl?.startsWith("https://"))
    return { status: verified, source: "verified", url: s.availabilitySourceUrl };
  if (s.docsLabel && (s.isSample || isOfficialDocsUrl(s.docsLabel.sourceUrl)))
    return { status: s.docsLabel.status, source: "docs", url: s.docsLabel.sourceUrl };
  return null;
}

export function isNew(publishedAt: number | null, now = Date.now()): boolean {
  return publishedAt !== null && publishedAt <= now + 86_400_000 && now - publishedAt <= NEW_WINDOW_DAYS * 86_400_000;
}
